import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, CircularProgress } from '@mui/material'
import { GridCellParams, GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'
import { ALLOWED_ROLES } from 'pages/Customers/sharedComponents/constant'
import { SearchToolbar } from 'pages/OTPacks/sharedComponents'
import {
  AllDistributorPromocodes,
  DistributorCodeTableRows,
  DistributorPromocodeDetails
} from 'types/api/faber/reward.type'

import AddBenefitDialog from './add_promo_code/AddBenefitDialog'
import AddDistributorDialog from './add_promo_code/AddDistributorDialog'
import AddDistributorPromoDialog from './add_promo_code/AddDistributorPromoDialog'
import AddRewardProductDialog from './add_promo_code/AddRewardProductDialog'
import UploadCsvDialog from './add_promo_code/UploadCsvDialog'
import DistributorActionMenu from './DistributorActionMenu'
import GenerateTrialCodes from './GenerateTrialCodes'
import PromocodeUsersDialog from './PromocodeUsersDialog'

export default function DistributorRewards() {
  const role = OrbitSession.get('role')

  const [rows, setRows] = useState<DistributorPromocodeDetails[]>([])
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [promocode, setPromocode] = useState<string>('')

  const [tempSearchValue, setTempSearchValue] = useState<string>('')

  const [sortDate] = useState<string>('id DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Promocode')

  const [pageCount, setPageCount] = useState(0)
  const [pageOffset, setPageOffset] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)

  const [rowCount, setRowCount] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Promocode']

  const queryOptions = useMemo(() => {
    return [searchValue, pageOffset, pageSize]
  }, [searchValue, pageOffset, pageSize])

  const handlePageChange = (newPage: number) => {
    const tmpOffset = (newPage - 1) * pageSize
    setPageOffset(tmpOffset)
    setPage(newPage -1)
  }

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  // fetch distributor promocodes
  const {
    data: getDistributorPromocodes,
    isLoading: isGetDistributorPromocodesLoading
  } = useQuery(
    ['getDistributorPromocodes', faberToken, ...queryOptions],
    async (): Promise<AllDistributorPromocodes> => {
      const allPlans = await RewardsAPI.getDistributorPromocodes(
        searchValue,
        pageSize, //limit
        pageOffset, //offset
        sortDate,
        faberToken
      )
      return allPlans
    },
    {
      onError: () => {
        toast.error('Error fetching distributor promocodes')
        setRows([])
        setIsLoading(false)
      }
    }
  )

  useEffect(() => {
    if (getDistributorPromocodes) {
      const { data, total } = getDistributorPromocodes.body
      const allDistributorCodes: DistributorCodeTableRows[] = data.map(
        ({
          _id,
          id,
          createdAt,
          updatedAt,
          distributorId,
          promocodeSummary,
          promocode,
          promocodeLink,
          distributorPrefix,
          batchPrefix,
          rewardProductId,
          maxClaims,
          maxConsumption,
          validityStartAt = 0,
          validityEndAt = 0,
          isExpired,
          totalConsumed,
          promoRule
        }) => ({
          _id,
          id,
          createdAt,
          updatedAt,
          distributorId,
          promocodeSummary,
          promocode,
          promocodeLink,
          distributorPrefix,
          batchPrefix,
          rewardProductId,
          maxClaims,
          maxConsumption,
          validFrom: new Date(validityStartAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Asia/Singapore'
          }),
          expiryAt: new Date(validityEndAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Asia/Singapore'
          }),
          validityStartAt,
          validityEndAt,
          isExpired,
          totalConsumed,
          promoRule
        })
      )

      const pageCount = Math.ceil(total / pageSize); 

      setPageCount(pageCount)
      setRowCount(total)
      setRows(allDistributorCodes)
      setIsLoading(false)
    }
  }, [getDistributorPromocodes])

  //search values
  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value)
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  const handleCellDoubleClick = (params: GridCellParams) => {
    const column = params.field
    if (column.match('totalConsumed')) {
      setPromocode(params.row.promocode)
      setOpenDialog(true)
    }
  }

  const handleViewUsersClick = (promocode: string) => {
    setPromocode(promocode)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setPromocode('')
    setOpenDialog(false)
  }

  const columns: GridColDef[] = [
    {
      field: 'Promocode',
      headerName: 'Promocode',
      width: 200,
      renderCell: (params: { row: DistributorPromocodeDetails }) => {
        return <NormalText>{params.row.promocode}</NormalText>
      }
    },
    {
      field: 'promocodeSummary',
      headerName: 'Description',
      flex: 1.4,
      minWidth: 100
    },
    {
      field: 'totalConsumed',
      headerName: 'Consumed',
      description: 'Double click each cell to see promocode users',
      flex: 0.2,
      minWidth: 100,
      renderCell: (params: { row: DistributorPromocodeDetails }) => {
        return <NormalText>{params.row.totalConsumed}</NormalText>
      }
    },
    {
      field: 'maxConsumption',
      headerName: 'Max Available',
      flex: 0.2,
      minWidth: 100
    },
    {
      field: 'isExpired',
      headerName: 'Expired',
      flex: 0.2,
      minWidth: 70
    },
    {
      field: 'validFrom',
      headerName: 'Valid From',
      flex: 0.5,
      minWidth: 140
    },
    {
      field: 'expiryAt',
      headerName: 'Expiry At',
      flex: 0.5,
      minWidth: 140
    },
    {
      sortable: false,
      field: 'actions',
      headerName: 'Actions',
      renderCell: (params: { row: DistributorPromocodeDetails }) => {
        return (
          <DistributorActionMenu
            row={params.row}
            promocode={promocode}
            openDialog={openDialog}
            handleCloseDialog={handleCloseDialog}
            handleViewUsersClick={handleViewUsersClick}
          />
        )
      },
      flex: 0.5,
      minWidth: 50,
      filterable: false,
      disableColumnMenu: true
    }
  ]

  return !isLoading ? (
    [ALLOWED_ROLES.OPS_OWNER, ALLOWED_ROLES.OPS].includes(role) ? (
      <Box>
        <ColorBox>
          <Box>
            <NormalText isBold>
              Please type promocode details in the search bar to begin
            </NormalText>
            <Box
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
            >
              <Box display='flex' flexDirection='row' gap={2}>
                <AddDistributorPromoDialog />
                <AddDistributorDialog />
                <AddRewardProductDialog />
                <AddBenefitDialog />
              </Box>
              <Box display={'flex'}>
                <GenerateTrialCodes />
                <UploadCsvDialog />
              </Box>
            </Box>
            {rows.length > 0 && (
              <NormalText isBold={true}>
                Double click each Consumed cell to see all promocode user IDs
              </NormalText>
            )}
            <MUIDataGrid
              getRowId={(row) => `${row.iccid}-${rows.indexOf(row)}`}
              rows={rows}
              pagination
              pageCount={pageCount}
              pageNumber={page+1}
              columns={columns}
              paginationMode='server'
              rowCount={rowCount}
              page={page}
              loading={isGetDistributorPromocodesLoading}
              pageSize={pageSize}
              onCellDoubleClick={handleCellDoubleClick}
              onPageChange={handlePageChange}
              disableColumnMenu={true}
              sortingMode='server'
              sortingOrder={['desc', 'asc']}
              components={{
                Toolbar: SearchToolbar
              }}
              componentsProps={{
                toolbar: {
                  searchBar: 'server',
                  serverSearchBarProps: {
                    onSubmit: onSearchSubmit,
                    onChange: onSearchValueChange,
                    options: searchOptions,
                    defaultValue: searchCriteria,
                    handleChange: handleSearchCriteriaChange
                  }
                }
              }}
            />
            {promocode && (
              <PromocodeUsersDialog
                promocode={promocode}
                openDialog={openDialog}
                handleCloseDialog={handleCloseDialog}
              />
            )}
          </Box>
        </ColorBox>
      </Box>
    ) : (
      <CircularProgress />
    )
  ) : (
    <CircularProgress />
  )
}
